import { get, set } from 'lodash';
import * as uuid from 'uuid';
import {
  ApplicantCompanyType,
  ApplicantEntity,
  BeneficialOwnerRole,
  BeneficialOwnerType,
  CompanyApplicantRelatedPartyType,
  CompanyStructure,
  State,
  TrustApplicantRelatedPartyType,
  TrustType,
} from '@lendi/core-constants';

import {
  ASSET_TYPE,
  COMPANY_CONTACT_TYPE,
  COMPANY_TYPE,
  EMPLOYMENT_TYPE,
  ENTITY_TYPE,
  EXPENSE_TYPE,
  FIELD_PATHS,
  FIELD_VALUES,
  FREQUENCY,
  INCOME_TYPE,
  LENDER_BANKING_FEATURES,
  LIABILITY_TYPE,
  MITIGANT_FACTOR,
  PAYMENT_ISSUES_TYPE,
  PERSON_TYPE,
  PRODUCT_PREFERENCES,
  REFINANCING_REASONS,
  RELATED_COMPANY_TYPE,
  REPAYMENT_OPTIONS,
  SIGNIFICANT_CHANGES_TYPE,
} from '@funnels/constants';
import {
  Address,
  AssetIncome,
  BrokerInterviews,
  BrokerInterviewsGuarantorWithId,
  BusinessFullDocIncome,
  BusinessLowDocIncome,
  CardLiability,
  CarLoanLiability,
  Company,
  CustomerData,
  Dependant,
  Employment,
  EstimatedExpense,
  ExpenseBase,
  Expenses,
  FinancialSituation,
  FormValues,
  FormValuesFunnelOne,
  GiftsAsset,
  GovtBenefitIncome,
  HelpHecsDebtLiability,
  HirePurchaseLiability,
  HomeContentsAsset,
  HomeLoanLiability,
  LeaseLiability,
  LineOfCreditLiability,
  ManagedFundsAsset,
  MiscIncome,
  Mitigant,
  OtherAsset,
  OtherIncome,
  OtherLiability,
  OverdraftLiability,
  PaygIncome,
  PaymentIssue,
  PersonalLoanLiability,
  PostProductSelectContract,
  ProductPreferences,
  PropertyAsset,
  RefinanceReason,
  RelatedParty,
  RepaymentOptions,
  SavingCheckAccountAsset,
  SharesAsset,
  ShortTermFinanceLiability,
  SignificantChange,
  StoreApplicant,
  StoreApplication,
  StoreCreditReport,
  StoreInvitation,
  SuperannuationAccountAsset,
  TaxDebtLiability,
  TermDepositAsset,
  VehicleAsset,
  YesNoIntend,
} from '@funnels/types';

export const INITIAL_CUSTOMER = (): CustomerData => ({
  id: undefined,
  firstName: undefined,
  lastName: undefined,
  mobileNumber: undefined,
  email: undefined,
  brand: undefined,
  isDeleted: undefined,
  updatedAt: undefined,
  createdAt: undefined,
});

export const INITIAL_ADDRESS = (): Address => ({
  postcode: undefined,
  state: undefined,
  formatted: undefined,
  city: undefined,
  country: undefined,
  standard: {
    streetName: undefined,
    streetNumber: undefined,
    streetType: undefined,
    level: undefined,
    unit: undefined,
    lotSection: undefined,
  },
});

export const INITIAL_APPLICANT = (customer?: CustomerData, properties?: Partial<StoreApplicant>): StoreApplicant => ({
  clientId: uuid.v4(),
  entity: FIELD_VALUES.PERSON,
  customerId: customer?.id,
  employmentType: undefined,
  hasSelfEmploymentProof: undefined,
  totalIncomeEstimate: undefined,
  contact: {
    currentAddress: undefined,
    emailAddress: customer?.email,
    mobileNumber: customer?.mobileNumber,
    previousAddresses: undefined,
  },
  applicantType: FIELD_VALUES.BORROWER,
  dateOfBirth: undefined,
  gender: undefined,
  maritalStatus: undefined,
  maritalDetails: undefined,
  residencyStatus: undefined,
  dependants: [],
  firstName: customer?.firstName,
  lastName: customer?.lastName,
  title: undefined,
  middleName: undefined,
  ...properties,
});

export const INITIAL_COMPANY_APPLICANT = (): StoreApplicant => ({
  clientId: uuid.v4(),
  entity: ApplicantEntity.Company,
  applicantType: FIELD_VALUES.BORROWER,
  companyName: 'Lendi Pvt Ltd',
  companyType: ApplicantCompanyType.PtyLtd,
  companyStructure: CompanyStructure.Corporation,
  industryCode: '0160',
  abn: '18611161856',
  acn: '611161856',
  registeredState: State.NSW,
  registeredDate: '2015-01-01',
  currentLender: 'CBA',
  legalEntitiesInGroup: 5,
  legalEntitiesWithCurrentLender: 4,
  monthsWithCurrentLender: 108,
  yearsOfTrading: '5-10',
  branch: 'Sydney',
  isDiversified: false,
  isPassiveEntity: false,
  isCommercialRelationship: false,
  hasMoreThan25PercentBusinessWithOneCustomer: false,
  moreThan25PercentBusinessWithOneCustomerDetails: 'Consumer business',
  hasOutstandingTax: false,
  outstandingTaxDetails: 'Good tax player',
  isPrincipalPlaceOfBusinessLeased: true,
  principalPlaceOfBusinessLeasedDetails: 'Sydney office',
  doesTermLoanNeed100PercentFunding: false,
  termLoanNeed100PercentFundingDetails: 'Private funding',
  isActingAsFranchise: YesNoIntend.YES,
  actingAsFranchiseDetails: 'Online business',
  isWorkingInImportExport: YesNoIntend.NO,
  workingInImportExportDetails: 'Consumer business',
  isInvestingInProperty: YesNoIntend.NO,
  investingInPropertyDetails: 'Mortgage lending',
});

export const INITIAL_TRUST_APPLICANT = (): StoreApplicant => ({
  clientId: uuid.v4(),
  entity: ApplicantEntity.Trust,
  applicantType: FIELD_VALUES.BORROWER,
  trustName: 'Trust Name',
  trustType: TrustType.CashManagementTrust,
  registeredDate: '2013-01-01',
  registeredCountry: 'Australia',
  registeredAddress: {
    city: 'Sydney',
    country: 'Australia',
    formatted: 'L9 50 Pitt St, Sydney, NSW 2000, Australia',
    postcode: '2000',
    standard: {
      level: 'L 9',
      streetName: 'Pitt',
      streetNumber: '50',
      streetType: 'St',
    },
    state: 'NSW',
  },
  mailingAddress: {
    city: 'Sydney',
    country: 'Australia',
    formatted: 'L9 50 Pitt St, Sydney, NSW 2000, Australia',
    postcode: '2000',
    standard: {
      level: 'L 9',
      streetName: 'Pitt',
      streetNumber: '50',
      streetType: 'St',
    },
    state: 'NSW',
  },
  industryCode: '0160',
  isPassiveEntity: false,
});

export const INITIAL_APPLICATION = (customer?: CustomerData): StoreApplication => ({
  hidden: undefined,
  clientId: uuid.v4(),
  customerId: get({ customers: [customer] }, FIELD_PATHS.CUSTOMER_ID(0), undefined),
  primaryApplicantId: undefined,
  preliminary: {
    primaryPurpose: undefined,
    // Refinance
    refinancingReasons: undefined,
    consolidateAmount: undefined,
    cashoutAmount: undefined,
    urgency: undefined,
    // New purchase
    purchaseReason: undefined,
    purchaseSituation: undefined,
    isSellingExistingProperty: undefined,
    // Borrowing power
    numberOfDependants: undefined,
    eligibleForFHOG: undefined,
    paymentType: undefined,
    hasSelfEmploymentProof: undefined,
    contributions: [
      {
        type: FIELD_VALUES.DEPOSIT,
        amount: undefined,
      },
    ],
  },
});

export const INITIAL_INVITE = (
  applicationId: string,
  applicantId: string,
  firstName: string,
  mobile: string
): StoreInvitation => ({
  clientId: uuid.v4(),
  applicationId,
  applicantId,
  firstName,
  mobile,
});

export const INITIAL_CREDIT_REPORTS = (applicantId: string): StoreCreditReport[] => [
  {
    code: 'CR_SUCCESS',
    applicantId,
    summary: {
      openedAccounts: {
        value: 1,
        dataLevel: 'Partial',
      },
      closedAccounts: {
        value: 2,
        dataLevel: 'Partial',
      },
      worstClosedInLast24Months: {
        value: -991,
        dataLevel: 'Comprehensive',
      },
      worstOpenAccountStatus: {
        value: 2,
        dataLevel: 'Comprehensive',
      },
      worstOpenInLast12Months: {
        value: -991,
        dataLevel: 'Comprehensive',
      },
      worstOpenInLast24Months: {
        value: -991,
        dataLevel: 'Comprehensive',
      },
      ageOfCreditFileInMths: {
        value: 154.17,
        dataLevel: 'Negative',
      },
      currentDirectorships: {
        value: 4,
        dataLevel: 'Negative',
      },
      previousDirectorships: {
        value: 0,
        dataLevel: 'Negative',
      },
      defaultsLast6Months: {
        value: 0,
        dataLevel: 'Negative',
      },
      defaultsLast12Months: {
        value: 6,
        dataLevel: 'Negative',
      },
      defaultsLast5To7Years: {
        value: 0,
        dataLevel: 'Negative',
      },
      insolvenciesAndActions: {
        value: 5,
        dataLevel: 'Negative',
      },
      enquiriesInLast5Years: {
        value: 11,
        dataLevel: 'Negative',
      },
      ownWorstAccountRepaymentInLast6Months: {
        dataLevel: 'Comprehensive',
        value: -991,
      },
      numberOfCommercialEnquiriesInLast60Months: {
        dataLevel: 'Negative',
        value: 2,
      },
      numberOfCommercialDefaultsInLast6Months: {
        dataLevel: 'Negative',
        value: 0,
      },
      numberOfCommercialDefaultsInLast12Months: {
        dataLevel: 'Negative',
        value: 1,
      },
      numberOfCommercialDefaultsInLast60Or84Months: {
        dataLevel: 'Negative',
        value: 3,
      },
    },
    numberOfIdentities: 1,
    lastRequestedAt: '2021-02-18T15:03:54.850+11:00',
    primaryMatch: {
      firstName: 'XX',
      middleName: 'YY',
      lastName: 'ZZZZ',
      gender: 'male',
      driversLicense: '1234567890',
      dateOfBirth: '1977-03-19',
    },
    scoreMasterScale: 226,
  },
];

export const INITIAL_PROPERTY_ASSET = (): PropertyAsset => ({
  clientId: uuid.v4(),
  // liabilityId: [],
  // liabilityClientId: [],
  type: ASSET_TYPE.PROPERTY,
  constructionDetails: undefined,
  estimatedValue: undefined,
  address: undefined,
  propertyStatus: FIELD_VALUES.ESTABLISHED,
  percentOwned: undefined,
  contractDetails: undefined,
  primaryPurpose: undefined,
  propertyType: undefined,
  zoneType: FIELD_VALUES.RESIDENTIAL,
  toBePurchased: false,
  toBeUsedAsSecurity: true,
  propertyExpenses: [],
});

export const INITIAL_SAVING_CHECK_ACCOUNT_ASSET = (): SavingCheckAccountAsset => ({
  clientId: uuid.v4(),
  type: ASSET_TYPE.SAVING_CHECK_ACCOUNT,
  estimatedValue: undefined,
  financialInstitution: undefined,
  percentOwned: undefined,
});

export const INITIAL_SUPER_ACCOUNT_ASSET = (): SuperannuationAccountAsset => ({
  clientId: uuid.v4(),
  // liabilityId: [],
  // liabilityClientId: [],
  type: ASSET_TYPE.SUPERANNUATION,
  estimatedValue: undefined,
  financialInstitution: undefined,
});

export const INITIAL_VEHICLE_ASSET = (): VehicleAsset => ({
  clientId: uuid.v4(),
  // liabilityId: [],
  // liabilityClientId: [],
  type: ASSET_TYPE.VEHICLE,
  percentOwned: {
    proportions: FIELD_VALUES.SPECIFIED,
    owners: [],
  },
  estimatedValue: undefined,
  make: undefined,
  model: undefined,
  year: undefined,
});

export const INITIAL_HOME_CONTENTS = (): HomeContentsAsset => ({
  clientId: uuid.v4(),
  // liabilityId: [],
  // liabilityClientId: [],
  type: ASSET_TYPE.HOME_CONTENTS,
  estimatedValue: undefined,
  percentOwned: {
    proportions: FIELD_VALUES.EQUAL,
    owners: [],
  },
});

export const INITIAL_TERM_DEPOSIT = (): TermDepositAsset => ({
  clientId: uuid.v4(),
  // liabilityId: [],
  // liabilityClientId: [],
  type: ASSET_TYPE.TERM_DEPOSIT,
  estimatedValue: undefined,
  financialInstitution: undefined,
  percentOwned: {
    proportions: FIELD_VALUES.EQUAL,
    owners: [],
  },
});

export const INITIAL_SHARES = (): SharesAsset => ({
  clientId: uuid.v4(),
  // liabilityId: [],
  // liabilityClientId: [],
  type: ASSET_TYPE.SHARES,
  estimatedValue: undefined,
  percentOwned: {
    proportions: FIELD_VALUES.EQUAL,
    owners: [],
  },
});

export const INITIAL_MANAGED_FUNDS = (): ManagedFundsAsset => ({
  clientId: uuid.v4(),
  // liabilityId: [],
  // liabilityClientId: [],
  type: ASSET_TYPE.MANAGED_FUNDS,
  estimatedValue: undefined,
  financialInstitution: undefined,
  percentOwned: {
    proportions: FIELD_VALUES.EQUAL,
    owners: [],
  },
});

export const INITIAL_GIFTS = (): GiftsAsset => ({
  clientId: uuid.v4(),
  // liabilityId: [],
  // liabilityClientId: [],
  type: ASSET_TYPE.GIFTS,
  estimatedValue: undefined,
  description: undefined,
  percentOwned: {
    proportions: FIELD_VALUES.EQUAL,
    owners: [],
  },
});

export const INITIAL_OTHER_ASSET = (): OtherAsset => ({
  clientId: uuid.v4(),
  // liabilityId: [],
  // liabilityClientId: [],
  type: ASSET_TYPE.OTHER_ASSET,
  estimatedValue: undefined,
  description: undefined,
  financialInstitution: undefined,
  percentOwned: {
    proportions: FIELD_VALUES.EQUAL,
    owners: [],
  },
});

export const INITIAL_DEPENDANT = (): Dependant => ({
  age: undefined,
  sharedWith: undefined,
});

export const INITIAL_HOME_LOAN_LIABILITY = (): HomeLoanLiability => ({
  clientId: uuid.v4(),
  assetId: [],
  assetClientId: [],
  annualInterestRate: undefined,
  clearingFromThisLoan: undefined,
  clearingFromOtherSource: undefined,
  creditLimit: undefined,
  financialInstitution: undefined,
  interestOnlyDuration: undefined,
  interestOnlyRemainingTerm: undefined, // ALWAYS YEARS
  newLimit: undefined,
  originalBalance: undefined,
  outstandingBalance: undefined,
  percentOwned: undefined,
  remainingTermDuration: undefined, // ALWAYS YEARS
  repaymentAmount: undefined,
  repaymentFrequency: FREQUENCY.MONTHLY,
  repaymentTypeDetails: FIELD_VALUES.PRINCIPAL_AND_INTEREST,
  toBeToppedUp: undefined,
  type: LIABILITY_TYPE.HOME_LOAN,
});

export const INITIAL_CARD_LIABILITY = (): CardLiability => ({
  clientId: uuid.v4(),
  assetId: [],
  assetClientId: [],
  annualInterestRate: undefined,
  creditLimit: undefined,
  financialInstitution: undefined,
  outstandingBalance: undefined,
  type: LIABILITY_TYPE.CREDIT_CARD,
});

export const INITIAL_PERSONAL_LOAN_LIABILITY = (): PersonalLoanLiability => ({
  type: LIABILITY_TYPE.PERSONAL_LOAN,
  clientId: uuid.v4(),
  assetId: [],
  assetClientId: [],
  creditLimit: undefined,
});

export const INITIAL_SHORT_TERM_FINANCE_LIABILITY = (): ShortTermFinanceLiability => ({
  type: LIABILITY_TYPE.SHORT_TERM_FINANCE,
  clientId: uuid.v4(),
  assetId: [],
  assetClientId: [],
});

export const INITIAL_LEASE_LIABILITY = (): LeaseLiability => ({
  type: LIABILITY_TYPE.LEASE,
  clientId: uuid.v4(),
  assetId: [],
  assetClientId: [],
});

export const INITIAL_HIRE_PURCHASE_LIABILITY = (): HirePurchaseLiability => ({
  type: LIABILITY_TYPE.HIRE_PURCHASE,
  clientId: uuid.v4(),
  assetId: [],
  assetClientId: [],
});

export const INITIAL_TAX_DEBT_LIABILITY = (): TaxDebtLiability => ({
  type: LIABILITY_TYPE.TAX_DEBT,
  financialInstitution: 'ATO',
  clientId: uuid.v4(),
  assetId: [],
  assetClientId: [],
});

export const INITIAL_LINE_OF_CREDIT_LIABILITY = (): LineOfCreditLiability => ({
  type: LIABILITY_TYPE.LINE_OF_CREDIT,
  clientId: uuid.v4(),
  assetId: [],
  assetClientId: [],
});

export const INITIAL_OVERDRAFT_LIABILITY = (): OverdraftLiability => ({
  type: LIABILITY_TYPE.OVERDRAFT,
  clientId: uuid.v4(),
  assetId: [],
  assetClientId: [],
});

export const INITIAL_HELP_HECS_DEBT_LIABILITY = (): HelpHecsDebtLiability => ({
  type: LIABILITY_TYPE.HELP_HECS_DEBT,
  financialInstitution: 'Other (HECS)',
  clientId: uuid.v4(),
  assetId: [],
  assetClientId: [],
});

export const INITIAL_OTHER_LIABILITY = (): OtherLiability => ({
  type: LIABILITY_TYPE.OTHER,
  clientId: uuid.v4(),
  assetId: [],
  assetClientId: [],
});

export const INITIAL_CAR_LOAN_LIABILITY = (): CarLoanLiability => ({
  clientId: uuid.v4(),
  assetId: [],
  assetClientId: [],
  balloonRepaymentAmount: undefined,
  financialInstitution: undefined,
  outstandingBalance: undefined,
  remainingTerm: undefined,
  repaymentAmount: undefined,
  repaymentFrequency: FREQUENCY.MONTHLY,
  type: LIABILITY_TYPE.CAR_LOAN,
});

export const INITIAL_PAYG_BASE_SALARY_INCOME = (): PaygIncome => ({
  clientId: uuid.v4(),
  amount: undefined,
  frequency: undefined,
  comments: undefined,
  type: INCOME_TYPE.PAYG_BASE_SALARY,
  percentOwned: undefined,
  employmentId: undefined,
});

export const INITIAL_PAYG_BONUS_INCOME = (): PaygIncome => ({
  clientId: uuid.v4(),
  amount: undefined,
  frequency: undefined,
  comments: undefined,
  type: INCOME_TYPE.PAYG_BONUS_INCOME,
  percentOwned: undefined,
  employmentId: undefined,
});

export const INITIAL_PAYG_COMMISSION_INCOME = (): PaygIncome => ({
  clientId: uuid.v4(),
  amount: undefined,
  frequency: undefined,
  comments: undefined,
  type: INCOME_TYPE.PAYG_COMMISSION_INCOME,
  percentOwned: undefined,
  employmentId: undefined,
});

export const INITIAL_PAYG_OVERTIME_INCOME = (): PaygIncome => ({
  clientId: uuid.v4(),
  amount: undefined,
  frequency: undefined,
  comments: undefined,
  type: INCOME_TYPE.PAYG_OVERTIME_INCOME,
  percentOwned: undefined,
  employmentId: undefined,
});

export const INITIAL_PAYG_CAR_ALLOWANCE = (): PaygIncome => ({
  clientId: uuid.v4(),
  amount: undefined,
  frequency: undefined,
  comments: undefined,
  type: INCOME_TYPE.PAYG_CAR_ALLOWANCE,
  percentOwned: undefined,
  employmentId: undefined,
});

export const INITIAL_PAYG_ALLOWANCES_TAXED = (): PaygIncome => ({
  clientId: uuid.v4(),
  amount: undefined,
  frequency: undefined,
  comments: undefined,
  type: INCOME_TYPE.PAYG_ALLOWANCES_TAXED,
  percentOwned: undefined,
  employmentId: undefined,
});

export const INITIAL_PAYG_ALLOWANCES_NOT_TAXED = (): PaygIncome => ({
  clientId: uuid.v4(),
  amount: undefined,
  frequency: undefined,
  comments: undefined,
  type: INCOME_TYPE.PAYG_ALLOWANCES_NOT_TAXED,
  percentOwned: undefined,
  employmentId: undefined,
});

export const INITIAL_PAYG_COMPANY_CAR = (): PaygIncome => ({
  clientId: uuid.v4(),
  amount: undefined,
  frequency: undefined,
  comments: undefined,
  type: INCOME_TYPE.PAYG_COMPANY_CAR,
  percentOwned: undefined,
  employmentId: undefined,
});

export const INITIAL_PAYG_NON_VOLUNTARY_DEDUCTIONS_PRE_TAX = (): PaygIncome => ({
  clientId: uuid.v4(),
  amount: undefined,
  frequency: undefined,
  comments: undefined,
  type: INCOME_TYPE.PAYG_NON_VOLUNTARY_DEDUCTIONS_PRE_TAX,
  percentOwned: undefined,
  employmentId: undefined,
});

export const INITIAL_PAYG_SALARY_SACRIFICE = (): PaygIncome => ({
  clientId: uuid.v4(),
  amount: undefined,
  frequency: undefined,
  comments: undefined,
  type: INCOME_TYPE.PAYG_SALARY_SACRIFICE,
  percentOwned: undefined,
  employmentId: undefined,
});

export const INITIAL_BUSINESS_INCOME = (
  companyClientId: string,
  hasFullDoc?: boolean
): BusinessFullDocIncome | BusinessLowDocIncome => ({
  clientId: uuid.v4(),
  companyClientId,

  type: hasFullDoc
    ? INCOME_TYPE.SELF_EMPLOYED_BUSINESS_INCOME_FULL_DOC
    : INCOME_TYPE.SELF_EMPLOYED_BUSINESS_INCOME_LOW_DOC,
});

export const INITIAL_SELF_EMPLOYED_BUSINESS_INCOME = (): PaygIncome => ({
  clientId: uuid.v4(),
  amount: undefined,
  frequency: undefined,
  comments: undefined,
  type: INCOME_TYPE.SELF_EMPLOYED_BUSINESS_INCOME,
  percentOwned: undefined,
  employmentId: undefined,
});

export const INITIAL_GOVT_BENEFIT = (): GovtBenefitIncome => ({
  clientId: uuid.v4(),
  amount: undefined,
  frequency: undefined,
  comments: undefined,
  type: INCOME_TYPE.GOVT_BENEFIT,
  percentOwned: {
    proportions: FIELD_VALUES.SPECIFIED,
    owners: [],
  },
  benefitType: undefined,
});

export const INITIAL_CHILD_MAINTENANCE = (): MiscIncome => ({
  clientId: uuid.v4(),
  amount: undefined,
  frequency: undefined,
  comments: undefined,
  type: INCOME_TYPE.CHILD_MAINTENANCE,
  percentOwned: {
    proportions: FIELD_VALUES.SPECIFIED,
    owners: [],
  },
});

export const INITIAL_FOREIGN_INCOME = (): MiscIncome => ({
  clientId: uuid.v4(),
  amount: undefined,
  frequency: undefined,
  comments: undefined,
  type: INCOME_TYPE.FOREIGN_INCOME,
  percentOwned: {
    proportions: FIELD_VALUES.SPECIFIED,
    owners: [],
  },
});

export const INITIAL_SUPER_ANNUITY_PENSION = (): MiscIncome => ({
  clientId: uuid.v4(),
  amount: undefined,
  frequency: undefined,
  comments: undefined,
  type: INCOME_TYPE.SUPER_ANNUITY_PENSION,
  percentOwned: {
    proportions: FIELD_VALUES.SPECIFIED,
    owners: [],
  },
});

export const INITIAL_WORKERS_COMPENSATION = (): MiscIncome => ({
  clientId: uuid.v4(),
  amount: undefined,
  frequency: undefined,
  comments: undefined,
  type: INCOME_TYPE.WORKERS_COMPENSATION,
  percentOwned: {
    proportions: FIELD_VALUES.SPECIFIED,
    owners: [],
  },
});

export const INITIAL_OTHER_INCOME = (): OtherIncome => ({
  clientId: uuid.v4(),
  amount: undefined,
  frequency: undefined,
  comments: undefined,
  type: INCOME_TYPE.OTHER_INCOME,
  percentOwned: {
    proportions: FIELD_VALUES.SPECIFIED,
    owners: [],
  },
  description: undefined,
});

export const INITIAL_INVESTMENT_INCOME = (): OtherIncome => ({
  clientId: uuid.v4(),
  amount: undefined,
  frequency: undefined,
  comments: undefined,
  type: INCOME_TYPE.INVESTMENT_INCOME,
  percentOwned: {
    proportions: FIELD_VALUES.SPECIFIED,
    owners: [],
  },
  description: undefined,
});

export const INITIAL_RENTAL_INCOME = (): AssetIncome => ({
  clientId: uuid.v4(),
  amount: undefined,
  frequency: undefined,
  comments: undefined,
  type: INCOME_TYPE.RENTAL_INCOME,
  assetId: undefined,
});

export const INITIAL_EMPLOYMENT = (): Employment => ({
  clientId: uuid.v4(),
  businessName: undefined,
  abn: undefined,
  abnVerified: undefined,
  privateOrPublic: undefined,
  startDate: undefined,
  endDate: undefined,
  role: undefined,
  foreignEmployed: undefined,
  employerAddress: undefined,
  onProbation: undefined,
  maternityLeaveReturnDate: undefined,
  type: undefined,
  employmentType: undefined,
  employmentBasis: undefined,
  maternityLeaveReturnBasis: undefined,
});

// TODO: use following method for employment initailisation
export const INITIAL_EMPLOYMENT_V2 = (applicantId: string, type: EMPLOYMENT_TYPE): Employment => ({
  clientId: uuid.v4(),
  applicantId,
  type,
});

export const INITIAL_COMPANY = (isSelfEmployed?: boolean): Company => ({
  clientId: uuid.v4(),
  type: !isSelfEmployed ? RELATED_COMPANY_TYPE.EMPLOYER : RELATED_COMPANY_TYPE.SELF_EMPLOYMENT_BUSINESS,
});

export const INITIAL_FORM_VALUE: (customer?: CustomerData) => FormValues = (customer?: CustomerData) => ({
  applications: [INITIAL_APPLICATION(customer)],
  applicants: [INITIAL_APPLICANT(customer)],
  assets: [INITIAL_PROPERTY_ASSET()],
  liabilities: [INITIAL_HOME_LOAN_LIABILITY()],
});

export const INITIAL_FUNNEL_REFINANCING_FORM_VALUE: (customer?: CustomerData) => FormValuesFunnelOne = (
  customer?: CustomerData
) => {
  const initialValues = INITIAL_FORM_VALUE(customer);
  const assetClientId = get(initialValues, FIELD_PATHS.ASSET_CLIENT_ID(0));
  const applicantId = get(initialValues, FIELD_PATHS.APPLICANT_CLIENT_ID(0));
  const company = INITIAL_COMPANY();
  const companyClientId = get({ companies: [company] }, FIELD_PATHS.COMPANY_CLIENT_ID(0));
  const primaryEmployment = {
    ...INITIAL_EMPLOYMENT(),
    ...{ companyClientId },
    applicantId,
    type: FIELD_VALUES.PRIMARY,
  };

  set(initialValues, FIELD_PATHS.LIABILITY_ASSET_CLIENT_ID(0), [assetClientId]);
  set(initialValues, FIELD_PATHS.LIABILITY_CLEARING_FROM_THIS_LOAN(0), true); // only the property from funnel 1 should pre-fill clearingFromThisLoan true
  set(initialValues, FIELD_PATHS.ASSET_TO_BE_PURCHASED(0), false);
  set(initialValues, FIELD_PATHS.APPLICATION_PAYMENT_TYPE(0), FIELD_VALUES.PRINCIPAL_AND_INTEREST);
  set(initialValues, FIELD_PATHS.EMPLOYMENTS(), [primaryEmployment]);

  set(initialValues, FIELD_PATHS.COMPANIES(), [company]);

  return {
    ...initialValues,
  };
};

export const INITIAL_FUNNEL_NEW_PURCHASE_FORM_VALUE: (customer?: CustomerData) => FormValuesFunnelOne = (
  customer?: CustomerData
) => {
  const initialValues = {
    applications: [INITIAL_APPLICATION(customer)],
    applicants: [INITIAL_APPLICANT(customer)],
    assets: [INITIAL_PROPERTY_ASSET()],
  };
  const applicantId = get(initialValues, FIELD_PATHS.APPLICANT_CLIENT_ID(0));
  const company = INITIAL_COMPANY();
  const companyClientId = get({ companies: [company] }, FIELD_PATHS.COMPANY_CLIENT_ID(0));
  const primaryEmployment = {
    ...INITIAL_EMPLOYMENT(),
    ...{ companyClientId },
    applicantId,
    type: FIELD_VALUES.PRIMARY,
  };

  set(initialValues, FIELD_PATHS.APPLICATION_PAYMENT_TYPE(0), FIELD_VALUES.PRINCIPAL_AND_INTEREST);
  set(initialValues, FIELD_PATHS.ASSET_TO_BE_PURCHASED(0), true);
  set(initialValues, FIELD_PATHS.EMPLOYMENTS(), [primaryEmployment]);

  set(initialValues, FIELD_PATHS.COMPANIES(), [company]);

  return initialValues;
};

export const INITIAL_FUNNEL_BORROWING_POWER_VALUE: (customer?: CustomerData) => FormValuesFunnelOne = (
  customer?: CustomerData
) => {
  const initialValues = {
    applications: [INITIAL_APPLICATION(customer)],
    applicants: [INITIAL_APPLICANT(customer)],
    assets: [INITIAL_PROPERTY_ASSET()],
  };

  set(initialValues, FIELD_PATHS.APPLICATION_PURCHASE_REASONS(0), FIELD_VALUES.LOAN_AFFORDABILITY);
  set(initialValues, FIELD_PATHS.APPLICATION_PURCHASE_SITUATION(0), FIELD_VALUES.EXPLORING_OPTIONS);
  set(initialValues, FIELD_PATHS.ASSET_TO_BE_PURCHASED(0), true);

  return initialValues;
};

export const INITIAL_EXPENSES = (): Expenses => ({
  applicantId: undefined,
  clientId: uuid.v4(),
  expenses: [],
});

export const INITIAL_EXPENSE = (): ExpenseBase => ({
  amount: undefined,
  frequency: FREQUENCY.MONTHLY,
  comments: undefined,
});

export const INITIAL_LIVING_EXPENSES_ESTIMATE = (): EstimatedExpense => ({
  amount: undefined,
  frequency: undefined,
  comments: undefined,
  type: EXPENSE_TYPE.LIVING_EXPENSES_ESTIMATE,
});

export const INITIAL_FINANCIAL_SITUATION = (applicationId: string, applicantId: string): FinancialSituation => ({
  clientId: uuid.v4(),
  applicationId,
  applicantId,
  mitigant: [],
  paymentIssues: [],
  retirementDetails: undefined,
  significantChanges: [],
});

export const INITIAL_FINANCIAL_SITUATION_SIGNIFICANT_CHANGE = (
  type: SIGNIFICANT_CHANGES_TYPE,
  frequency?: FREQUENCY
): SignificantChange => ({
  type,
  frequency,
});

export const INITIAL_FINANCIAL_SITUATION_MITIGANT = (factor: MITIGANT_FACTOR): Mitigant => ({
  factor,
});

export const INITIAL_FINANCIAL_SITUATION_PAYMENTISSUE_TYPE = (type: PAYMENT_ISSUES_TYPE): PaymentIssue => ({
  type,
});

export const INITIAL_PRODUCT_PREFERENCE = (applicationId: string): ProductPreferences => ({
  clientId: uuid.v4(),
  applicationId,
  isRefinance: undefined,
  extraRepayments: undefined,
  lenderBankingFeatures: [],
  reason: [],
  repaymentTypeDetails: { details: [] },
  otherPreferences: undefined,
  refinanceReasons: [],
  repaymentOptions: undefined,
  loanTerm: undefined,
});

export const INITIAL_PRODUCT_PREFERENCE_REFINANCE_REASON = (reason: REFINANCING_REASONS): RefinanceReason => ({
  reason,
});

export const INITIAL_PRODUCT_PREFERENCE_REASON = (reason: PRODUCT_PREFERENCES): PRODUCT_PREFERENCES => reason;

export const INITIAL_PRODUCT_PREFERENCE_REPAYMENT_OPTIONS = (option: REPAYMENT_OPTIONS): RepaymentOptions => ({
  option,
});

export const INITIAL_PRODUCT_LENDER_BANKING_FEATURE = (
  lenderBankingFeatures: LENDER_BANKING_FEATURES
): LENDER_BANKING_FEATURES => lenderBankingFeatures;

export const INITIAL_VISIT_CONTACT_RELATED_PARTY = (applicationId: string, assetId: string): RelatedParty => ({
  clientId: uuid.v4(),
  applicationId,
  assetId,
});

export const INITIAL_ACCOUNTANT_RELATED_PARTY = (applicationId: string, employmentId: string): RelatedParty => ({
  clientId: uuid.v4(),
  applicationId,
  type: COMPANY_TYPE.Accountant,
  employmentId,
});

export const INITIAL_SOLICITOR_RELATED_PARTY = (applicationId: string, assetId: string): RelatedParty => ({
  clientId: uuid.v4(),
  applicationId,
  type: COMPANY_TYPE.Solicitor,
  assetId,
});

export const INITIAL_LICENSED_BUILDER_RELATED_PARTY = (applicationId: string, assetId: string): RelatedParty => ({
  clientId: uuid.v4(),
  applicationId,
  type: COMPANY_TYPE.LicensedBuilder,
  assetId,
});

export const INITIAL_SPOUSE_RELATED_PARTY = (applicantId: string): RelatedParty => ({
  clientId: uuid.v4(),
  applicantId,
  type: PERSON_TYPE.Spouse,
});

export const INITIAL_TRUST_SETTLOR_RELATED_PARTY = (trustApplicantId: string, clientId?: string): RelatedParty => ({
  clientId: clientId || uuid.v4(),
  trustApplicantId,
  type: TrustApplicantRelatedPartyType.TrustSettlor,
});

export const INITIAL_TRUSTEE_RELATED_PARTY = (
  trustApplicantClientId: string,
  applicantClientId: string
): RelatedParty => ({
  clientId: uuid.v4(),
  applicantClientId,
  type: TrustApplicantRelatedPartyType.Trustee,
  trustApplicantClientId,
});

export const INITIAL_TRUST_BENEFICIARY_RELATED_PARTY = (
  trustApplicantClientId: string,
  applicantClientId: string
): RelatedParty => ({
  clientId: uuid.v4(),
  applicantClientId,
  type: TrustApplicantRelatedPartyType.TrustBeneficiary,
  trustApplicantClientId,
});

export const INITIAL_TRUST_BENEFICIAL_OWNER_RELATED_PARTY = (
  trustApplicantClientId: string,
  applicantClientId: string
): RelatedParty => ({
  clientId: uuid.v4(),
  applicantClientId,
  type: TrustApplicantRelatedPartyType.TrustBeneficialOwner,
  trustApplicantClientId,
  ownerType: BeneficialOwnerType.Controller,
});

export const INITIAL_DIRECTOR_RELATED_PARTY = (
  companyApplicantClientId: string,
  applicantClientId: string
): RelatedParty => ({
  clientId: uuid.v4(),
  applicantClientId,
  type: CompanyApplicantRelatedPartyType.Director,
  companyApplicantClientId,
});

export const INITIAL_COMPANY_BENEFICIAL_OWNER_RELATED_PARTY = (
  companyApplicantClientId: string,
  applicantClientId: string
): RelatedParty => ({
  clientId: uuid.v4(),
  applicantClientId,
  type: CompanyApplicantRelatedPartyType.CompanyBeneficialOwner,
  companyApplicantClientId,
  ownerType: BeneficialOwnerType.Controller,
  ownerRole: BeneficialOwnerRole.Trustee,
});

export const INITIAL_COMPANY_CONTACT_RELATED_PARTY = (
  companyApplicantClientId: string,
  companyContactType: COMPANY_CONTACT_TYPE,
  companyApplicantId?: string,
  applicantClientId?: string
) => ({
  clientId: uuid.v4(),
  type: CompanyApplicantRelatedPartyType.CompanyContact,
  companyApplicantClientId,
  companyContactType,
  ...(applicantClientId && { applicantClientId }),
  ...(companyApplicantId && { companyApplicantId }),
});

export const INITIAL_BROKER_INTERVIEW = (applicationId: string): BrokerInterviews => ({
  clientId: uuid.v4(),
  applicationId,
});

export const INITIAL_APPLICANT_POST_PRODUCT_SELECT = (applicantId: string): PostProductSelectContract => ({
  entityType: ENTITY_TYPE.APPLICANT,
  entityId: applicantId,
  questions: [],
});

export const INITIAL_BROKER_INTERVIEW_GUARANTOR = (applicantId: string): BrokerInterviewsGuarantorWithId => ({
  applicantId,
});
